<template>
  <b-container v-if="userProfileLoaded">
    <!-- ===== LINHA DE CARDS PRINCIPAIS ===== -->
    <div class="row mb-4">
      <div v-if="hasPermission('cards_aulas_avulsas')"
        class="col-md-3 mb-3"
        @click="showModalAulasAvulsas = true"
        style="cursor: pointer"
      >
        <DashboardCard
          titulo="Aulas Avulsas"
          subtitulo="Agendadas"
          :valor="overview.totalaulasavulsasagendadas"
          cor="#9B59B6"
          icone="bi-bookmark-star"
        />
      </div>
      <div v-if="hasPermission('cards_aulas_reposicao')"
        class="col-md-3 mb-3"
        @click="showModalAulasReposicao = true"
        style="cursor: pointer"
      >
        <DashboardCard
          titulo="Aulas de Reposição"
          subtitulo="Agendadas"
          :valor="overview.totalaulasreposicaoagendadas ?? 0"
          cor="#343a40"
          icone="bi-clock-history"
        />
      </div>
      <div v-if="hasPermission('cards_aulas_experimentais')"
        class="col-md-3 mb-3"
        @click="showModalAulasSemana = true"
        style="cursor: pointer"
      >
        <DashboardCard
          titulo="Aulas Experimentais"
          subtitulo="Agendadas"
          :valor="overview.totalaulasexperimentais"
          cor="#4A90E2"
          icone="bi-calendar-check"
        />
      </div>

      <div
        v-if="hasPermission('cards_faltas_avisadas')"
        class="col-md-3 mb-3"
        @click="showModalFaltasAvisadas = true"
        style="cursor: pointer"
      >
        <DashboardCard
          titulo="Faltas Avisadas"
          subtitulo="Próximas"
          :valor="overview.totalfaltasavisadas ?? 0"
          cor="#6c757d"
          icone="bi-bell"
        />
      </div>

      <div v-if="hasPermission('cards_planos_vencer')"
        class="col-md-3 mb-3" 
        @click="abrirModalPlanos" 
        style="cursor:pointer"
      >
        <DashboardCard
          titulo="Planos a Vencer"
          subtitulo="Próximos 30 dias"
          :valor="overview.totalplanosvencer"
          cor="#E74C3C"
          icone="bi-card-text"
        />
      </div>
      <div
        v-if="hasPermission('cards_prospects')"
        class="col-md-3 mb-3"
      >
        <div
          v-if="hasFeature('DASHBOARD_PROSPECTS_HOJE')"
          @click="showModalProspects = true"
          style="cursor: pointer"
        >
          <DashboardCard
            titulo="Prospects"
            subtitulo="Hoje e pendentes"
            :valor="overview.totalprospectshoje"
            cor="#F5A623"
            icone="bi-person-plus"
          />
        </div>

        <div
          v-else
          class="dashboard-card-blocked"
          :title="getFeatureMessage('DASHBOARD_PROSPECTS_HOJE')"
          @click="handleBlockedFeature('DASHBOARD_PROSPECTS_HOJE')"
        >
          <DashboardCard
            titulo="Prospects"
            subtitulo="Hoje e pendentes"
            :valor="0"
            cor="#F5A623"
            icone="bi-person-plus"
          />
        </div>
      </div>
      <div v-if="hasPermission('cards_matriculas_ativas')"
        class="col-md-3 mb-3" 
        style="cursor: default;"
      >
        <DashboardCard
          titulo="Matrículas Ativas"
          subtitulo="Atualmente"
          :valor="overview.totalmatriculasativas"
          cor="#1ABC9C"
          icone="bi-person-fill"
        />
      </div>

      <div
        v-if="hasPermission('cards_trocas_nivel_turma')"
        class="col-md-3 mb-3"
        @click="abrirModalTrocasNivelTurma"
        style="cursor: pointer"
      >
        <DashboardCard
          titulo="Trocas de Nível"
          subtitulo="Pendentes"
          :valor="overview.totaltrocasniveisturmapendentes ?? 0"
          cor="#8B4513"
          icone="bi-arrow-left-right"
        />
      </div>
      
    </div>
    <!-- ===== FIM LINHA DE CARDS PRINCIPAIS ===== -->

    <!-- linha dos dois gráficos lado a lado -->
    <div v-if="hasPermission('graficos_dashboard')" class="row mb-4">
      <div class="col-md-6">
        <ReceitasReceberChart
          :data-inicio="dataInicio"
          :data-fim="dataFim"
          :redraw="true"
          :key="`rr-${dataInicio}-${dataFim}`"
        />
      </div>
      <div class="col-md-6">
        <ReceitasPorTipoPie
          :data-inicio="dataInicio"
          :data-fim="dataFim"
        />
      </div>
    </div>

    <!-- ===== NOVA LINHA: AGENDA DIÁRIA | SUPERVISÃO | QUEDA ===== -->
    <div class="row align-items-start">
      <!-- 1) Agenda Diária de Turmas -->
      <div v-if="hasPermission('agendas_dashboard')" class="col-md-4 mb-3">
        <b-card
          header="Agenda Diária de Turmas"
          header-class="bg-black text-white"
        >
          <template #header>
            <h6 class="mb-0 p-1">Agenda Diária de Turmas</h6>
          </template>

          <div class="d-flex justify-content-end align-items-center mb-3">
            <button class="icon-button" @click="changeDay(-1)">
              <i class="bi bi-chevron-left"></i>
            </button>
            <button v-if="!isToday" class="icon-button mx-2" @click="goToToday">
              Hoje
            </button>
            <button class="icon-button" @click="changeDay(1)">
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>

          <div class="text-center mb-3">
            <h6 class="mb-0">{{ formattedDate }}</h6>
          </div>

          <b-table
            :key="isInstructor"
            :items="filteredData"
            :fields="headers"
            striped hover responsive="sm" bordered small
            empty-text="Nenhuma turma encontrada."
          >
            <template #cell(horario)="row">
              <span
                :class="{
                  'text-danger':
                    row.item.__contagem
                    && row.item.__contagem.tem_chamada === false
                    && totalAgendados(row.item.__contagem) > 0
                }"
              >
                {{ formatTime(row.item.horario) }}
              </span>
            </template>

            <!-- Nível + contadores (matriculados / reposição / experimental / avulsa) -->
            <template #cell(nivel_nome)="row">
              <span>{{ row.item.nivel_nome }}</span>
              <template v-if="row.item.__contagem">
                <span v-if="typeof row.item.__contagem.matriculados === 'number'">
                  &nbsp;- {{ row.item.__contagem.matriculados }}
                </span>
                <span v-if="row.item.__contagem.reposicao > 0" class="text-primary">
                  &nbsp;({{ row.item.__contagem.reposicao }})
                </span>
                <span v-if="row.item.__contagem.experimental > 0" class="text-success">
                  &nbsp;({{ row.item.__contagem.experimental }})
                </span>
                <span v-if="row.item.__contagem.avulsa > 0" class="text-warning">
                  &nbsp;({{ row.item.__contagem.avulsa }})
                </span>
              </template>
            </template>

            <!-- 1) slot sempre declarado -->
            <template #cell(acao)="row">
              <i
                v-if="isInstructor"
                class="bi bi-pencil-square"
                title="Realizar Chamada"
                style="cursor: pointer; color: #007bff;"
                @click="abrirRegistrarChamada(row.item)"
              ></i>
            </template>

          </b-table>
        </b-card>
      </div>

      <!-- 2) Supervisão -->
      <div v-if="hasPermission('supervisoes_dashboard')" class="col-md-4 mb-3">
        <b-card
          header="Supervisões"
          header-class="bg-primary text-white"
        >
          <template #header>
            <h6 class="mb-0 p-1">Supervisões</h6>
          </template>

          <div v-if="supervisaoRows.length > 0" class="table-scroll">
            <b-table
              v-if="supervisaoRows.length > 0"
              :items="supervisaoRows"
              :fields="supervisaoFields"
              striped
              hover
              responsive="sm"
              small
              empty-text="Nenhuma supervisão pendente."
            >
              <template #cell(acao)="data">
                <!-- Ícone principal: edição ou criação -->
                <template v-if="data.item.mudar_nivel">
                  <!-- Mudar Nível -->
                  <i
                    :class="[
                      'bi bi-pencil-square me-2',
                      data.item.realizada === 'Sim' ? 'text-warning' : 'text-info'
                    ]"
                    title="Mudar Nível"
                    @click="
                      data.item.finalizada
                        ? abrirMudarNivelReadOnly(data.item)
                        : abrirMudarNivel(data.item)
                    "
                  ></i>
                </template>
                <template v-else>
                  <!-- Orientação (abre SEMPRE em edição, mesmo se realizada === 'Sim') -->
                  <i
                    :class="[
                      'bi bi-star me-2',
                      data.item.realizada === 'Sim' ? 'text-warning' : 'text-primary'
                    ]"
                    title="Orientação"
                    @click="abrirOrientacao(data.item)"
                  ></i>
                </template>

                <!-- Ícone para cancelar supervisão -->
                <i
                  class="bi bi-x-circle text-danger"
                  title="Cancelar Supervisão"
                  @click="abrirCancelar(data.item)"
                ></i>
              </template>
            </b-table>
          </div>
          <div v-else class="text-muted">Nenhuma supervisão pendente.</div>
        </b-card>
      </div>

      <!-- 3) Queda -->
      <!-- <div v-if="hasPermission('quedas_dashboard')" class="col-md-4 mb-3">
        <b-card
          header="Quedas não comunicadas"
          header-class="bg-danger text-white"
        >
          <template #header>
            <h6 class="mb-0 p-1">Quedas não comunicadas</h6>
          </template>
          <div v-if="quedaRows.length > 0" class="table-scroll">
            <b-table
              v-if="quedaRows.length > 0"
              :items="quedaRows"
              :fields="quedaFields"
              striped
              hover
              responsive="sm"
              small
              empty-text="Nenhuma queda pendente."
            >
              <template #cell(acao)="data">
                <i
                  class="bi bi-envelope"
                  style="cursor: pointer; color: #dc3545;"
                  title="Comunicar família"
                  @click="abrirComunicacao(data.item)"
                ></i>
              </template>
            </b-table>
          </div>
          <div v-else class="text-muted">Nenhuma queda pendente.</div>
        </b-card>
      </div> -->

      <!-- 3.1) Protocolos de Queda (Pendentes) -->
      <div v-if="hasPermission('protocolos_queda_dashboard')" class="col-md-4 mb-3">
        <b-card header-class="text-white" class="card-protocolo-queda">
          <template #header>
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="mb-0 p-1">Protocolos de Queda</h6>
              <span class="badge bg-light text-dark">
                Pendentes: {{ overview.totalprotocolosqueda_pendentes ?? 0 }}
              </span>
            </div>
          </template>

          <!-- Contadores por cor -->
          <div class="d-flex flex-wrap gap-2 mb-2">
            <span class="badge bg-danger">🔴 {{ protocolosQuedaContadores.vermelho }}</span>
            <span class="badge bg-warning text-dark">🟡 {{ protocolosQuedaContadores.amarelo }}</span>
            <span class="badge badge-purple">🟣 {{ protocolosQuedaContadores.roxo }}</span>
            <span class="badge bg-success">🟢 {{ protocolosQuedaContadores.verde }}</span>
          </div>

          <div v-if="protocolosQuedaRows.length > 0" class="table-scroll">
            <b-table
              v-if="protocolosQuedaRows.length > 0"
              :items="protocolosQuedaRows"
              :fields="protocolosQuedaFields"
              striped
              hover
              responsive="sm"
              small
              empty-text="Nenhum protocolo pendente."
            >
              <!-- bolinha de cor -->
              <template #cell(nivel)="data">
                <span
                  class="dot"
                  :class="data.item.nivel_cor"
                  :title="data.item.nivel_cor"
                ></span>
              </template>

              <template #cell(status)="data">
                <span class="text-nowrap">{{ data.item.status }}</span>
                <div class="text-muted small">
                  <span v-if="data.item.data_aula">{{ formatDateBR(data.item.data_aula) }}</span>
                  <span v-if="data.item.horario"> • {{ formatHoraHM(data.item.horario) }}</span>
                </div>
              </template>

              <template #cell(acao)="data">
                <i
                  class="bi bi-eye"
                  style="cursor: pointer; color: #0d6efd;"
                  title="Abrir protocolo"
                  @click="abrirProtocoloQuedaDashboard(data.item)"
                ></i>
              </template>
            </b-table>
          </div>

          <div v-else class="text-muted">Nenhum protocolo pendente.</div>
        </b-card>
      </div>

      <!-- 4) Faltas -->
      <div v-if="hasPermission('faltas_dashboard')" class="col-md-4 mb-3">
        <div
          v-if="!hasFeature('RELATORIO_FALTAS_NAO_COMUNICADAS_DASHBOARD')"
          class="dashboard-card-blocked"
          :title="getFeatureMessage('RELATORIO_FALTAS_NAO_COMUNICADAS_DASHBOARD')"
          @click="handleBlockedFeature('RELATORIO_FALTAS_NAO_COMUNICADAS_DASHBOARD')"
        >
          <b-card
            header="Faltas não comunicadas"
            header-class="bg-orange text-white"
          >
            <template #header>
              <h6 class="mb-0 p-1">Faltas não comunicadas</h6>
            </template>

            <div class="text-muted">Módulo exclusivo para o Plano Performance.</div>
          </b-card>
        </div>

        <b-card
          v-else
          header="Faltas não comunicadas"
          header-class="bg-orange text-white"
        >
          <template #header>
            <h6 class="mb-0 p-1">Faltas não comunicadas</h6>
          </template>

          <div v-if="faltaRows.length > 0" class="table-scroll">
            <b-table
              :items="faltaRows"
              :fields="faltasFields"
              striped
              hover
              responsive="sm"
              small
              empty-text="Nenhuma falta pendente."
            >
              <template #cell(aluno)="data">
                {{ data.item.aluno }}
                <span v-if="data.item.total_faltas > 1" class="text-muted">
                  ({{ data.item.total_faltas }} faltas)
                </span>
              </template>

              <template #cell(dias_pendentes)="data">
                <span :class="{ 'text-danger': data.item.dias_pendentes >= 7 }">
                  {{ data.item.dias_pendentes }}
                </span>
              </template>

              <template #cell(acao)="data">
                <i
                  class="bi bi-envelope"
                  style="cursor:pointer; color:#fd7e14;"
                  title="Comunicar família"
                  @click="abrirComunicacaoFalta(data.item)"
                />
              </template>
            </b-table>
          </div>

          <div v-else class="text-muted">Nenhuma falta pendente.</div>
        </b-card>
      </div>

      <!-- Renovação Antecipada -->
      <div v-if="hasPermission('renovacao_antecipada_dashboard')" class="col-md-4 mb-3">
        <div
          v-if="!hasFeature('RELATORIO_RENOVACAO_ANTECIPADA_DASHBOARD')"
          class="dashboard-card-blocked"
          :title="getFeatureMessage('RELATORIO_RENOVACAO_ANTECIPADA_DASHBOARD')"
          @click="handleBlockedFeature('RELATORIO_RENOVACAO_ANTECIPADA_DASHBOARD')"
        >
          <b-card
            header="Renovação Antecipada"
            header-class="bg-emerald text-white"
          >
            <template #header>
              <h6 class="mb-0 p-1">Renovação Antecipada</h6>
            </template>

            <div class="text-muted">Módulo exclusivo para o Plano Performance.</div>
          </b-card>
        </div>

        <b-card
          v-else
          header="Renovação Antecipada"
          header-class="bg-emerald text-white"
        >
          <template #header>
            <h6 class="mb-0 p-1">Renovação Antecipada</h6>
          </template>

          <div v-if="renovacaoRows.length > 0" class="table-scroll">
            <b-table
              :items="renovacaoRows"
              :fields="renovacaoFields"
              striped
              hover
              responsive="sm"
              small
              empty-text="Nenhuma matrícula a vencer em 10–30 dias."
            >
              <template #cell(matricula)="data">
                {{ data.item.matricula_id }}
              </template>

              <template #cell(acao)="data">
                <i
                  class="bi bi-envelope"
                  style="cursor:pointer; color:#1ABC9C;"
                  title="Enviar e-mail de renovação"
                  @click="abrirModalEmailRenovacao(data.item)"
                />
              </template>
            </b-table>
          </div>

          <div v-else class="text-muted">
            Nenhuma matrícula a vencer em 10–30 dias.
          </div>
        </b-card>
      </div>

      <!-- Troca de Nível - Envio ao Responsável -->
      <div v-if="hasPermission('troca_nivel_envio_dashboard')" class="col-md-4 mb-3">
        <b-card
          header="Troca de Nível - Envio ao Responsável"
          header-class="bg-brown text-white"
        >
          <template #header>
            <h6 class="mb-0 p-1">Troca de Nível - Envio ao Responsável</h6>
          </template>

          <div v-if="trocaEnvioRows.length > 0" class="table-scroll">
            <b-table
              v-if="trocaEnvioRows.length > 0"
              :items="trocaEnvioRows"
              :fields="trocaEnvioFields"
              striped
              hover
              responsive="sm"
              small
              empty-text="Nenhum envio pendente."
            >
              <template #cell(acao)="data">
                <i
                  class="bi bi-envelope"
                  style="cursor:pointer; color:#8B4513;"
                  title="Enviar e-mail ao responsável"
                  @click="abrirModalEmailTrocaNivel(data.item)"
                />
              </template>
            </b-table>
          </div>

          <div v-else class="text-muted">Nenhum envio pendente.</div>
        </b-card>
      </div>



      <!-- Card: Envio de Avaliações -->
      <div class="col-12 col-lg-8">
        <CardEnvioAvaliacoes v-if="hasPermission('enviar_avaliacoes')" :per-page="10" />
      </div>

    </div>
    <!-- ===== FIM NOVA LINHA ===== -->
  </b-container>

  <!-- MODAIS DE SUPERVISÃO -->
  <SupervisaoOrientacao
    v-if="showModalOrientacao"
    :mostrar="showModalOrientacao"
    :supervisao="selectedSupervisao"
    :modo-visualizacao="modoVisualizacao"
    @close="closeModalOrientacao"
    @salvou="refreshList"
  />
  <SupervisaoMudarNivel
    v-if="showModalMudarNivel"
    :mostrar="showModalMudarNivel"
    :supervisao="selectedSupervisao"
    :modo-visualizacao="modoVisualizacao"
    @close="closeModalMudarNivel"
    @salvou="refreshList"
  />
  <SupervisaoCancelar
    v-if="showModalCancelar"
    :mostrar="showModalCancelar"
    :supervisao="selectedSupervisao"
    :modo-visualizacao="modoLeituraCancelar"
    @close="closeModalCancelar"
    @cancelado="refreshList"
  />

  <ComunicacaoQueda
    v-if="showComunicacao"
    :mostrar="showComunicacao"
    :queda="selectedQueda"
    @close="closeComunicacao"
    @salvou="onSalvouComunicacao"
  />

  <ProtocoloQueda
    v-if="showModalProtocoloQueda"
    :visible="showModalProtocoloQueda"
    :registro="selectedProtocoloQueda"
    :readOnly="true"
    :workflow="true"
    :saveToBackend="true"
    @close="fecharModalProtocoloQueda"
  />

  <ComunicacaoFalta
    v-if="showComunicacaoFalta"
    :mostrar="showComunicacaoFalta"
    :falta="selectedFalta"
    @close="closeComunicacaoFalta"
    @salvou="onSalvouComunicacaoFalta"
  />

  <RegistrarChamada
    v-if="showRegistrarChamada"
    :turma="selectedTurmaParaChamada"
    :data-inicial="dataInicialRegistrarChamada"
    @salvou="fecharRegistrarChamada"
    @cancelar="fecharRegistrarChamada"
  />

  <PlanosAVencer
    :mostrar="showModalPlanos"
    :planos="planosAVencer"
    @fechar="showModalPlanos = false"
  />

  <AulasExperimentaisSemana
    :mostrar="showModalAulasSemana"
    :aulas="aulasSemana"
    @fechar="showModalAulasSemana = false"
  />

  <ProspectsHoje
    :mostrar="showModalProspects"
    :prospects="prospectsHoje"
    @fechar="showModalProspects = false"
  />

  <AulasAvulsasModal
    v-if="showModalAulasAvulsas"
    :mostrar="showModalAulasAvulsas"
    :aulas="aulasAvulsas"
    @fechar="showModalAulasAvulsas = false"
    @detalhar="detalharAulaAvulsa"
  />

  <AulasReposicaoModal
    v-if="showModalAulasReposicao"
    :mostrar="showModalAulasReposicao"
    :aulas="aulasReposicao"
    @fechar="showModalAulasReposicao = false"
    @detalhar="detalharAulaReposicao"
    @reagendar="abrirReagendarReposicao"
  />

  <FaltasAvisadasModal
    v-if="showModalFaltasAvisadas"
    :mostrar="showModalFaltasAvisadas"
    :aulas="faltasAvisadas"
    @fechar="showModalFaltasAvisadas = false"
  />

  <ReagendarAulaReposicao
    v-if="showModalReagendarReposicao"
    :show="showModalReagendarReposicao"
    :aula="selectedAulaReposicao"
    @close="() => { showModalReagendarReposicao = false; selectedAulaReposicao = null }"
    @saved="onReagendamentoSalvo"
  />

  <EmailRenovacaoAntecipada
    :show="showEmailRenovacao"
    :registro="registroRenovacao"
    @close="fecharModalEmailRenovacao"
    @sent="onRenovacaoEmailSent"
  />

  <TrocarNivelTurma
    v-if="showModalTrocarNivelTurma"
    :mostrar="showModalTrocarNivelTurma"
    :registros="trocasPendentes"
    @fechar="showModalTrocarNivelTurma = false"
  />

  <EmailTrocaNivel
    v-if="showEmailTrocaNivel"
    :visible="showEmailTrocaNivel"
    :aluno="registroTrocaNivel"
    :empresa-id="1"
    :supervisao-id="registroTrocaNivel?.supervisao_id"
    @close="fecharModalEmailTrocaNivel"
    @sent="onCertificadoEnviado"
  />

</template>

<script>
import { BContainer, BCard, BTable } from "bootstrap-vue-next";
import axios from "@/services/axios";
import DashboardCard from "@/components/Dashboard/DashboardCard.vue";
import ReceitasReceberChart from '@/components/Dashboard/ReceitasReceberChart.vue'
import ReceitasPorTipoPie  from '@/components/Dashboard/ReceitasPorTipoPie.vue'
import SupervisaoOrientacao  from '@/components/modals/SupervisaoOrientacao.vue';
import SupervisaoMudarNivel  from '@/components/modals/SupervisaoMudarNivel.vue';
import SupervisaoCancelar    from '@/components/modals/SupervisaoCancelar.vue';
import ComunicacaoQueda from "../modals/ComunicacaoQueda.vue";
import ComunicacaoFalta from "../modals/ComunicacaoFalta.vue";
import RegistrarChamada from '@/components/modals/RegistrarChamada.vue';
import PlanosAVencer from '@/components/modals/PlanosAVencer.vue';
import AulasExperimentaisSemana from '@/components/modals/AulasExperimentaisSemana.vue';
import ProspectsHoje from '@/components/modals/ProspectsHoje.vue';
import AulasAvulsasModal from '@/components/modals/AulasAvulsasModal.vue';
import AulasReposicaoModal from '@/components/modals/AulasReposicaoModal.vue';
import FaltasAvisadasModal from '@/components/modals/FaltasAvisadasModal.vue';
import ReagendarAulaReposicao from '@/components/modals/ReagendarAulaReposicao.vue';
import EmailRenovacaoAntecipada from '@/components/modals/EmailRenovacaoAntecipada.vue' // novo
import CardEnvioAvaliacoes from '@/components/Dashboard/CardEnvioAvaliacoes.vue'
import TrocarNivelTurma from '@/components/modals/TrocarNivelTurma.vue'; // (novo - modal a ser criado)
import EmailTrocaNivel from '@/components/modals/EmailTrocaNivel.vue' // novo
import ProtocoloQueda from '@/components/modals/ProtocoloQueda.vue';


export default {
  name: 'DashboardPage',
  components: {
    BContainer,
    BCard,
    BTable,
    DashboardCard,
    ReceitasReceberChart,
    ReceitasPorTipoPie,
    SupervisaoOrientacao,
    SupervisaoMudarNivel,
    SupervisaoCancelar,
    ComunicacaoQueda,
    ComunicacaoFalta,
    RegistrarChamada,
    PlanosAVencer,
    AulasExperimentaisSemana,
    ProspectsHoje,
    AulasAvulsasModal,
    AulasReposicaoModal,
    FaltasAvisadasModal,
    ReagendarAulaReposicao,
    EmailRenovacaoAntecipada,
    CardEnvioAvaliacoes,
    TrocarNivelTurma,
    EmailTrocaNivel,
    ProtocoloQueda,
  },
  data() {
    const ano = new Date().getFullYear()
    return {
      overview: {
        totalaulasexperimentais: 0,
        totalplanosvencer: 0,
        totalprospectshoje: 0,
        totalmatriculasativas: 0,
        totalaulasavulsasagendadas: 0,
        totalaulasreposicaoagendadas: 0,
        totalfaltasavisadas: 0,
        supervisaolista: [],
        quedalista: [],
      },
      dataInicio: `${ano}-01-01`,
      dataFim:    `${ano}-12-31`,
      showModalOrientacao: false,
      showModalMudarNivel: false,
      showModalCancelar: false,
      modoVisualizacao: false,
      modoLeituraCancelar: false,
      selectedSupervisao: null,
      showComunicacao: false,
      selectedQueda: null,
      showRegistrarChamada: false,
      selectedTurmaParaChamada: null,
      dataInicialRegistrarChamada: '',
      selectedAlunosMatriculados: [],
      selectedAlunosExperimentais: [],
      selectedAlunosAvulsos: [],
      showModalPlanos: false,
      showModalAulasSemana: false,
      planosAVencer: [],    // <–– novo
      aulasSemana: [],    // vai receber overview.aulasexperimentaissemana
      showModalProspects: false,
      prospectsHoje: [],
      showModalAulasAvulsas: false,
      aulasAvulsas: [],
      showModalAulasReposicao: false,
      aulasReposicao: [],
      showModalFaltasAvisadas: false,
      faltasAvisadas: [],
      showModalReagendarReposicao: false,
      selectedAulaReposicao: null,
      showEmailRenovacao: false,
      registroRenovacao: null,
      showModalTrocarNivelTurma: false, // <<< fora de overview
      trocasPendentes: [],              // <<< fora de overview

      showModalProtocoloQueda: false,
      selectedProtocoloQueda: null,

      // tabela do card Protocolos de Queda
      protocolosQuedaFields: [
        { key: 'nivel', label: '', thStyle: { width: '40px' }, tdClass: 'text-center' },
        { key: 'aluno', label: 'Aluno' },
        { key: 'status', label: 'Status', thStyle: { width: '160px' }, tdClass: 'text-nowrap' },
        { key: 'acao', label: 'Ação', thClass: 'text-center', tdClass: 'text-center', thStyle: { width: '60px' } },
      ],

      // ==> Campos da tabela de Supervisão
      supervisaoFields: [
        { key: "aluno", label: "Aluno" },
        { key: "instrutor", label: "Instrutor" },
        { key: "acao", label: "Ações" },
      ],

      // ==> Campos da tabela de Queda
      quedaFields: [
        { key: "matricula", label: "Matrícula" },
        { key: "aluno",     label: "Aluno" },
        {
          key:     "acao",
          label:   "Ação",
          thClass: "text-center",
          tdClass: "text-center"
        }
      ],

      // Dados da Agenda Diária
      currentDate: new Date(),
      data: [],
      headers: [],
      contagensAgenda: [],   // <<< NOVO: contagens isoladas, sem depender do overview

      // Faltas não comunicadas
      faltasFields: [
        { key: "matricula",     label: "Matrícula" },
        { key: "aluno",         label: "Aluno" },
        {
          key: "dias_pendentes",
          label: "Dias",
          thClass: "text-center",
          tdClass: "text-center"
        },
        {
          key: "acao",
          label: "Ação",
          thClass: "text-center",
          tdClass: "text-center"
        }
      ],

      showComunicacaoFalta: false,
      selectedFalta: null,
      faltasListaLocal: [], // fallback se overview não trouxer

      renovacaoFields: [
        { key: 'matricula',         label: 'Matrícula',     thStyle: { width: '90px' },  tdClass: 'text-nowrap' },
        { key: 'aluno_nome',        label: 'Aluno',         thStyle: { width: '100%' },  tdClass: 'text-wrap'   }, // ocupa o resto
        { key: 'dias_para_vencer',  label: 'Dias p/vencer', thStyle: { width: '120px' }, tdClass: 'text-center' },
        { key: 'acao',              label: 'Ação',          thStyle: { width: '70px' },  tdClass: 'text-center' },
      ],
      renovacaoRows: [],
      // --- Troca de Nível - Envio ao Responsável ---
      showEmailTrocaNivel: false,
      registroTrocaNivel: null,
      trocaEnvioFields: [
        { key: 'matricula', label: 'Matrícula', thStyle: { width: '100px' }, tdClass: 'text-nowrap' },
        { key: 'aluno',     label: 'Aluno' },
        { key: 'acao',      label: 'Ação',     thStyle: { width: '80px'  }, tdClass: 'text-center' },
      ],
      trocaEnvioRows: [],
    };
  },
  computed: {

    isInstructor() {
      const perfil = this.$globalState.userProfile || ''
      return perfil.toLowerCase().includes('instrutor')
    },

    userProfileLoaded() {
      return !!this.$globalState.userProfile;
    },

    tableHeaders() {
      const cols = [
        { key: "horario", label: "Horário" },
        { key: "nivel_nome", label: "Nível - Qtd. Alunos" }
      ];
      if (this.isInstructor) {
        cols.push({
          key: "acao",
          label: "Ação",
          thClass: "text-center",   // centraliza o <th>
          tdClass: "text-center"    // centraliza cada <td>
        });
      } else {
        cols.push({ key: "instrutor_nome", label: "Instrutor" });
      }
      return cols;
    },

    protocolosQuedaContadores() {
      const c = this.overview?.protocolosqueda_contadores || {};
      return {
        vermelho: Number(c.vermelho ?? 0),
        amarelo:  Number(c.amarelo  ?? 0),
        roxo:     Number(c.roxo     ?? 0),
        verde:    Number(c.verde    ?? 0),
      };
    },

    protocolosQuedaRows() {
      const lista = Array.isArray(this.overview?.protocolosqueda_pendentes_lista)
        ? this.overview.protocolosqueda_pendentes_lista
        : [];

      // Mapeia para o formato da tabela do card
      return lista.map(r => ({
        id: r.id,
        aluno: r.aluno_nome,
        status: r.status,
        nivel_cor: r.nivel_cor || 'normal',
        matricula_id: r.matricula_id,
        data_aula: r.data_aula,
        horario: r.horario,
        instrutor: r.instrutor
      }));
    },

    // Converte cada string "Aluno - Instrutor" em objeto { aluno, instrutor }
    supervisaoRows() {
      return this.overview.supervisaolista;
    },
    // Converte cada string "Aluno - Matrícula: X" ou "Aluno - Aula experimental"
    // em objeto { matricula, aluno }
    quedaRows() {
      const lista = Array.isArray(this.overview?.quedalista)
        ? this.overview.quedalista
        : [];
      return lista.map(q => ({
        id: q.id,
        aluno: q.aluno_nome,
        matricula: q.matricula_id || 'Aula experimental',
        observacoes: q.observacoes,
        descricao: q.descricao
      }));
    },

    faltaRows() {
      // base original vinda do overview ou do fallback local
      const baseRaw =
        this.overview && Array.isArray(this.overview.faltalista)
          ? this.overview.faltalista
          : this.faltasListaLocal;

      let base = Array.isArray(baseRaw) ? baseRaw : [];

      // 🔥 REMOVE alunos sem matrícula
      base = base.filter(
        (f) => f.matricula_id !== null && f.matricula_id !== undefined
      );

      if (base.length === 0) return [];

      const grupos = new Map();

      const MS_DIA = 1000 * 60 * 60 * 24;

      // "Hoje" em Brasília convertido para UTC apenas para cálculo de diferença em dias
      const hojeUTC = this.getTodayBrasiliaUTC();

      const parseDateOnlyToUTC = (valor) => {
        if (!valor) return null;
        const s = String(valor).slice(0, 10); // garante 'YYYY-MM-DD'
        const [y, m, d] = s.split("-").map(Number);
        if (!y || !m || !d) return null;
        return Date.UTC(y, m - 1, d);
      };

      for (const f of base) {
        const matriculaId = f.matricula_id;
        const alunoNome = f.aluno_nome || "Sem nome";
        const key = `${matriculaId}|${alunoNome}`;

        if (!grupos.has(key)) {
          grupos.set(key, {
            id: f.id, // ID representante
            aluno: alunoNome,
            matricula: matriculaId,
            total_faltas: 0,
            faltasGrupo: [],
            menorDataUTC: null, // menor data de falta em UTC
          });
        }

        const g = grupos.get(key);
        g.total_faltas += 1;
        g.faltasGrupo.push({
          id: f.id,
          data_falta: f.data_falta,
          observacao: f.observacao || "",
        });

        const faltaUTC = parseDateOnlyToUTC(f.data_falta);
        if (faltaUTC !== null) {
          if (g.menorDataUTC === null || faltaUTC < g.menorDataUTC) {
            g.menorDataUTC = faltaUTC;
          }
        }
      }

      const lista = Array.from(grupos.values());

      // calcula dias_pendentes por grupo (usando sempre UTC)
      for (const g of lista) {
        if (g.menorDataUTC !== null) {
          const diffMs = hojeUTC - g.menorDataUTC;
          const diffDias = Math.floor(diffMs / MS_DIA);
          g.dias_pendentes = Math.max(diffDias, 0);
        } else {
          g.dias_pendentes = 0;
        }
        delete g.menorDataUTC;
      }

      // ordena por dias_pendentes desc, depois por aluno
      lista.sort((a, b) => {
        if (b.dias_pendentes !== a.dias_pendentes) {
          return b.dias_pendentes - a.dias_pendentes;
        }
        return a.aluno.localeCompare(b.aluno, "pt-BR");
      });

      return lista;
    },

    formattedDate() {
      return new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(this.currentDate);
    },
    isToday() {
      return this.formatDateToBrasiliaString(this.currentDate) === this.getTodayBrasiliaString();
    },

    // Mapa de contagens vindas do overview.agendadiariaturmas
    contagensAgendaMap() {
      const lista = Array.isArray(this.contagensAgenda) ? this.contagensAgenda : [];
      const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

      const map = new Map();
      for (const r of lista) {
        const turma   = r.turma_id ?? r.turma;
        const dia     = norm(r.dia || r.dias || r.dia_semana);
        const horario = String(r.horario || '');
        if (!turma || !horario || !dia) continue;

        const key = `${turma}|${dia}|${horario}`;
        map.set(key, {
          matriculados: Number(r.matriculados ?? 0),
          reposicao:    Number(r.reposicao    ?? 0),
          experimental: Number(r.experimental ?? 0),
          avulsa:       Number(r.avulsa       ?? 0),
          // >>> NOVO: status de chamada vindo do endpoint /operacional/turmas-instrutor/dashboard
          tem_chamada:  r.tem_chamada === true, // força booleano exato
        });
      }
      return map;
    },

    filteredData() {
      const dayName = this.getWeekdayBrasilia(this.currentDate);
      const norm = (s) =>
        String(s || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();

      const diaNorm = norm(dayName);

      return this.data
        .filter((item) => item.dia_semana === dayName)
        .map((item) => {
          const turma   = item.turma_id ?? item.turma;
          const horario = String(item.horario || '');
          const key     = `${turma}|${diaNorm}|${horario}`;
          const cont    = this.contagensAgendaMap.get(key) || null;
          return cont ? { ...item, __contagem: cont } : item;
        })
        .sort((a, b) => String(a.horario).localeCompare(String(b.horario)));
    },

  },

  created() {
    // Inicializa headers
    this.headers = this.tableHeaders;
  },
  
  watch: {
    isInstructor() {
      this.headers = this.tableHeaders;
    },
    currentDate() {
      // A agenda precisa atualizar só as contagens/itens do dia
      this.fetchAgendaContagensPorData(); // ✅
    }
  },

  methods: {

    getDatePartsBrasilia(date = new Date()) {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(date);

      const map = Object.fromEntries(
        parts.map((part) => [part.type, part.value])
      );

      return {
        year: Number(map.year),
        month: Number(map.month),
        day: Number(map.day),
        yyyyMmDd: `${map.year}-${map.month}-${map.day}`,
      };
    },

    getTodayBrasiliaString() {
      return this.getDatePartsBrasilia(new Date()).yyyyMmDd;
    },

    getTodayBrasiliaUTC() {
      const { year, month, day } = this.getDatePartsBrasilia(new Date());
      return Date.UTC(year, month - 1, day);
    },

    formatDateToBrasiliaString(date) {
      return this.getDatePartsBrasilia(date).yyyyMmDd;
    },

    getWeekdayBrasilia(date) {
      return new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        weekday: 'long',
      }).format(date);
    },

    async fetchOverview() {
      try {
        const resp = await axios.get('/dashboard/overview');
        this.overview = resp.data;

        this.planosAVencer = resp.data.planosavencer;
        this.aulasSemana = resp.data.aulasexperimentaisagendadas;

        if (this.hasFeature('DASHBOARD_PROSPECTS_HOJE')) {
          this.prospectsHoje = resp.data.prospectshoje || [];
          this.overview.totalprospectshoje = resp.data.totalprospectshoje ?? 0;
        } else {
          this.prospectsHoje = [];
          this.overview.totalprospectshoje = 0;
        }

        this.overview.totalfaltasavisadas = resp.data.totalfaltasavisadas ?? 0;
        this.faltasAvisadas = resp.data.faltasavisadas ?? [];

        if (!this.hasFeature('RELATORIO_FALTAS_NAO_COMUNICADAS_DASHBOARD')) {
          this.overview.faltalista = [];
          this.faltasListaLocal = [];
        }

        if (this.hasFeature('RELATORIO_RENOVACAO_ANTECIPADA_DASHBOARD')) {
          this.overview.totalrenovacoesantecipadas = resp.data.totalrenovacoesantecipadas ?? 0;
          this.overview.renovacoesantecipadas = resp.data.renovacoesantecipadas || [];

          this.renovacaoRows = this.overview.renovacoesantecipadas.map(r => ({
            matricula_id: r.matricula_id,
            aluno_nome: r.aluno_nome,
            dias_para_vencer: r.dias_para_vencer,
            email_responsavel: r.email_responsavel,
            responsavel_id: r.responsavel_id,
            responsavel_nome: r.responsavel_nome,
            data_vencimento: r.data_vencimento,
          }));
        } else {
          this.overview.totalrenovacoesantecipadas = 0;
          this.overview.renovacoesantecipadas = [];
          this.renovacaoRows = [];
        }

        this.overview.totalaulasavulsasagendadas = resp.data.totalaulasavulsasagendadas;
        this.aulasAvulsas = resp.data.aulasavulsasagendadas;

        this.overview.totalaulasreposicaoagendadas = resp.data.totalaulasreposicaoagendadas;
        this.aulasReposicao = resp.data.aulasreposicaoagendadas;

        this.overview.totaltrocasniveisturmapendentes =
          resp.data.totaltrocasniveisturmapendentes ?? 0;

        const listaTrocasEnvio = Array.isArray(resp.data.trocasnivelenvio)
          ? resp.data.trocasnivelenvio
          : [];

        this.trocaEnvioRows = listaTrocasEnvio.map(r => ({
          matricula: r.matricula,
          aluno: r.aluno,
          aluno_id: r.aluno_id,
          supervisao_id: r.supervisao_id,
        }));

        this.overview.quedalista = Array.isArray(this.overview.quedalista)
          ? this.overview.quedalista
          : [];

      } catch (error) {
        console.error("Erro ao buscar overview:", error);
      }
    },
    async fetchData() {
      try {
        const response = await axios.get(
          `/cadastros/dias-horarios-turmas/dashboard`
        );
        this.data = response.data.data;
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
      }
    },

    changeDay(days) {
      this.currentDate.setDate(this.currentDate.getDate() + days);
      this.currentDate = new Date(this.currentDate);
      this.fetchAgendaContagensPorData(); // <<< ADICIONE ESTA LINHA
    },

    goToToday() {
      this.currentDate = new Date();
      this.fetchAgendaContagensPorData(); // <<< ADICIONE ESTA LINHA
    },

    formatTime(time) {
      if (!time) return "-";
      const [hours, minutes] = time.split(":");
      return `${hours}:${minutes}`;
    },

    formatDateBR(value) {
      if (!value) return '-';

      const s = String(value).slice(0, 10); // 'YYYY-MM-DD'
      const [y, m, d] = s.split('-');
      if (!y || !m || !d) return value;

      return `${d}/${m}/${y}`;
    },

    formatHoraHM(value) {
      if (!value) return '-';

      // aceita 'HH:mm:ss' ou 'HH:mm'
      const [h, min] = String(value).split(':');
      if (h == null || min == null) return value;
      return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
    },

    hasFeature(featureKey) {
      if (!this.$globalState.featuresLoaded) {
        return true;
      }
      return !!this.$globalState.featureFlags?.[featureKey]?.enabled;
    },

    getFeatureMessage(featureKey) {
      return this.$globalState.featureFlags?.[featureKey]?.message
        || 'Módulo exclusivo para o Plano Performance';
    },

    handleBlockedFeature(featureKey) {
      const message = this.getFeatureMessage(featureKey);

      if (typeof this.$alert === 'function') {
        this.$alert(message, 'Plano');
        return;
      }

      alert(message);
    },    

    async fetchAgendaContagensPorData() {
      try {
        const dataStr = this.formatDateToBrasiliaString(this.currentDate);

        const resp = await axios.get('/operacional/turmas-instrutor/dashboard', {
          params: { data: dataStr }
        });

        this.contagensAgenda = Array.isArray(resp?.data?.data) ? resp.data.data : [];
      } catch (err) {
        console.error('Erro ao buscar contagens da agenda:', err);
        this.contagensAgenda = [];
      }
    },

    badgeColorByNivel(nivel) {
      // classes bootstrap
      if (nivel === 'vermelho') return 'bg-danger';
      if (nivel === 'amarelo')  return 'bg-warning text-dark';
      if (nivel === 'roxo')     return 'bg-purple';
      if (nivel === 'verde')    return 'bg-success';
      return 'bg-secondary';
    },

    async abrirProtocoloQuedaDashboard(item) {
      try {
        const resp = await axios.get(`/operacional/alunos-quedas/${item.id}`);
        // seu endpoint GET /:id retorna res.json(result.rows[0])

        const r = resp.data || {};
        this.selectedProtocoloQueda = {
          ...r,
          chamada_id: r.chamada_id ?? item.chamada_id ?? null,
          aluno_nome: r.aluno_nome ?? item.aluno ?? item.aluno_nome ?? '',
        };
        this.showModalProtocoloQueda = true;
      } catch (err) {
        console.error('Erro ao abrir protocolo de queda:', err);
        this.$alert?.('Não foi possível abrir o protocolo.', 'Erro');
      }
    },

    fecharModalProtocoloQueda() {
      this.showModalProtocoloQueda = false;
      this.selectedProtocoloQueda = null;
      this.fetchOverview();
    },

    abrirOrientacao(item) {
      this.selectedSupervisao = item;
      this.modoVisualizacao = false;
      this.showModalOrientacao = true;
    },
    abrirOrientacaoReadOnly(item) {
      this.selectedSupervisao = item;
      this.modoVisualizacao = true;
      this.showModalOrientacao = true;
    },
    abrirMudarNivel(item) {
      this.selectedSupervisao = item;
      this.modoVisualizacao = false;
      this.showModalMudarNivel = true;
    },
    abrirMudarNivelReadOnly(item) {
      this.selectedSupervisao = item;
      this.modoVisualizacao = true;
      this.showModalMudarNivel = true;
    },
    abrirCancelar(item) {
      this.selectedSupervisao = item;
      this.modoLeituraCancelar = false;
      this.showModalCancelar = true;
    },
    abrirCancelarReadOnly(item) {
      this.selectedSupervisao = item;
      this.modoLeituraCancelar = true;
      this.showModalCancelar = true;
    },
    closeModalOrientacao() {
      this.showModalOrientacao = false;
      this.selectedSupervisao = null;
    },
    closeModalMudarNivel() {
      this.showModalMudarNivel = false;
      this.selectedSupervisao = null;
    },
    closeModalCancelar() {
      this.showModalCancelar = false;
      this.selectedSupervisao = null;
      this.modoLeituraCancelar = false;
    },

    async abrirModalTrocasNivelTurma() {
      this.showModalTrocarNivelTurma = true; // abre já
      try {
        const { data } = await axios.get('/operacional/supervisoes/trocas-pendentes', { params: { page: 1, limit: 50 } });
        this.trocasPendentes = Array.isArray(data?.data) ? data.data : (data || []);
      } catch (err) {
        console.error('Erro ao carregar trocas pendentes:', err);
        this.trocasPendentes = [];
      }
    },

    totalAgendados(cont) {
      const m = Number(cont?.matriculados ?? 0);
      const r = Number(cont?.reposicao    ?? 0);
      const e = Number(cont?.experimental ?? 0);
      const a = Number(cont?.avulsa       ?? 0);
      return m + r + e + a;
    },

    onConcluiuTrocaNivel({ id } = {}) {
      // Atualiza lista e contador localmente após efetivar troca no modal
      if (id) {
        this.trocasPendentes = this.trocasPendentes.filter(r => r.id !== id);
        if (typeof this.overview.totaltrocasniveisturmapendentes === 'number') {
          this.overview.totaltrocasniveisturmapendentes =
            Math.max(0, this.overview.totaltrocasniveisturmapendentes - 1);
        }
      } else {
        // fallback: recarregar tudo
        this.fetchOverview?.();
      }
      this.showModalTrocarNivelTurma = false;
    },

    abrirModalPlanos() {
      this.showModalPlanos = true;
    },
    fecharModalPlanos() {
      this.showModalPlanos = false;
    },

    abrirModalEmailRenovacao(reg) {
      this.registroRenovacao = reg
      this.showEmailRenovacao = true
    },
    fecharModalEmailRenovacao() {
      this.showEmailRenovacao = false
      this.registroRenovacao = null
    },

    abrirModalEmailTrocaNivel(item) {
      // item vem da tabela trocaEnvioRows: { matricula, aluno, aluno_id }
      this.registroTrocaNivel = {
        id: item.aluno_id,
        nome: item.aluno,
        // pode setar um padrão ou deixar em branco para o modal editar
        nivel_texto: 'NÍVEL I',        // ajuste se já souber o nível aprovado
        responsavel_email: '',          // opcional: preencher via fetch
        supervisao_id:    item.supervisao_id, // 👈 novo
        matricula_id:     item.matricula      // (útil também)
      };

      // (Opcional) buscar e-mail do responsável principal
      // try {
      //   const resp = await axios.get(`/alunos/${item.aluno_id}/responsavel-principal`);
      //   this.registroTrocaNivel.responsavel_email = resp.data?.email || '';
      // } catch (e) { console.warn('Sem e-mail do responsável', e); }

      this.showEmailTrocaNivel = true;
    },

    fecharModalEmailTrocaNivel() {
      this.showEmailTrocaNivel = false;
      this.registroTrocaNivel = null;
    },

    onCertificadoEnviado() {
      // Remover da lista o registro que acabou de ser enviado (se aplicável)
      if (this.registroTrocaNivel?.id) {
        this.trocaEnvioRows = this.trocaEnvioRows.filter(
          r => r.aluno_id !== this.registroTrocaNivel.id
        );
      }
      this.fecharModalEmailTrocaNivel();

      // (Opcional) atualizar contador do overview, se houver
      if (typeof this.overview?.totaltrocasniveisturmapendentes === 'number') {
        this.overview.totaltrocasniveisturmapendentes = Math.max(
          0,
          this.overview.totaltrocasniveisturmapendentes - 1
        );
      }
    },

    onRenovacaoEmailSent(payload) {
      // Se o modal não enviar payload, usa o item que já está selecionado
      const item = payload || this.registroRenovacao
      const id   = item?.matricula_id

      // 1) Fecha o modal
      this.fecharModalEmailRenovacao()

      if (id == null) return

      // 2) Remove da tabela do card
      this.renovacaoRows = this.renovacaoRows.filter(r => r.matricula_id !== id)

      // 3) Mantém o overview coerente (lista e contador)
      if (this.overview && Array.isArray(this.overview.renovacoesantecipadas)) {
        this.overview.renovacoesantecipadas =
          this.overview.renovacoesantecipadas.filter(r => r.matricula_id !== id)
      }
      if (typeof this.overview?.totalrenovacoesantecipadas === 'number') {
        this.overview.totalrenovacoesantecipadas = Math.max(
          0,
          this.overview.totalrenovacoesantecipadas - 1
        )
      }

      // (Opcional) se preferir recalcular tudo:
      // this.fetchOverview()
    },

    refreshList() {
      // opcional no dashboard: recarregar overview ou outra ação
      this.fetchOverview();
      this.closeModalOrientacao();
      this.closeModalMudarNivel();
      this.closeModalCancelar();
    },

    async abrirComunicacao(quedaItem) {
      const id = quedaItem.id;
      try {
        const resp = await axios.get(`/operacional/quedas/${id}`);
        this.selectedQueda = resp.data.data;
        this.showComunicacao = true;
      } catch (err) {
        console.error('Erro ao carregar dados da queda:', err);
      }
    },

    closeComunicacao() {
      this.showComunicacao = false;
      this.selectedQueda = null;
    },
    onSalvouComunicacao() {
      this.fetchOverview();
      this.closeComunicacao();
    },

    async abrirComunicacaoFalta(grupoItem) {
      // grupoItem vem de faltaRows (já agrupado)
      const idRepresentante = grupoItem.id;

      try {
        const resp = await axios.get(`/operacional/faltas/${idRepresentante}`);

        // junta os dados detalhados da falta principal
        // com a lista de faltas do grupo que montamos no computed
        this.selectedFalta = {
          ...resp.data.data,
          faltasGrupo: grupoItem.faltasGrupo || []
        };

        this.showComunicacaoFalta = true;
      } catch (err) {
        console.error('Erro ao carregar dados da falta:', err);
      }
    },

    closeComunicacaoFalta() {
      this.showComunicacaoFalta = false;
      this.selectedFalta = null;
    },
    onSalvouComunicacaoFalta() {
      const id = this.selectedFalta?.id;

      // Remove da lista atual (overview ou fallback)
      if (id) {
        if (this.overview?.faltalista && Array.isArray(this.overview.faltalista)) {
          this.overview.faltalista = this.overview.faltalista.filter(f => f.id !== id);
        }
        if (Array.isArray(this.faltasListaLocal)) {
          this.faltasListaLocal = this.faltasListaLocal.filter(f => f.id !== id);
        }
      }

      this.closeComunicacaoFalta();

      // Recarrega os dados do dashboard
      this.fetchOverview();
      if (!this.overview?.faltalista && this.fetchFaltasNaoComunicadas) {
        this.fetchFaltasNaoComunicadas();
      }
    },
    // (opcional) fallback: se overview não trouxer faltas, busque direto
    async fetchFaltasNaoComunicadas() {
      try {
        const resp = await axios.get('/operacional/faltas/nao-comunicadas');
        this.faltasListaLocal = resp.data?.data ?? [];
      } catch (err) {
        console.error('Erro ao buscar faltas não comunicadas:', err);
      }
    },

    async abrirRegistrarChamada(item) {
      // 1) Pega o ID da turma (pode vir em item.turma_id ou item.turma)
      const turmaId = item.turma_id ?? item.turma;
      if (!turmaId) {
        console.error("Não encontrei turma_id nem turma em", item);
        return;
      }

      // 2) Monta os params para buscar nível e listas de alunos
      const dataSelecionada = this.formatDateToBrasiliaString(this.currentDate);

      const params = {
        data:       dataSelecionada,
        turma_id:   turmaId,
        dia_semana: item.dia_semana || item.dias,
        horario:    item.horario
      };

      let resp;
      try {
        resp = await axios.get(
          `/operacional/turmas-instrutor/detalhes`,
          { params }
        );
      } catch (err) {
        console.error("Erro ao buscar detalhes da turma:", err);
        return alert("Não foi possível carregar os detalhes.");
      }

      // 3) Extraímos apenas o que precisamos para montar `selectedTurmaParaChamada`
      const { nivel_id, nivel } = resp.data;

      // 4) Monta o objeto exatamente como o ListarTurmasInstrutor faz
      this.selectedTurmaParaChamada = {
        turma:      turmaId,
        nivel_id,
        nivel,
        instrutor: item.instrutor_nome || item.instrutor,
        dias:      item.dia_semana || item.dias,
        horario:   item.horario
      };

      // 5) Abre o modal (o modal vai carregar os alunos quando você selecionar a data)
      this.dataInicialRegistrarChamada = dataSelecionada;
      this.showRegistrarChamada = true;
    },

    async fecharRegistrarChamada() {
      this.showRegistrarChamada = false;
      this.selectedTurmaParaChamada = null;
      this.dataInicialRegistrarChamada = '';

      this.fetchData();
      await this.fetchOverview();

      // ✅ atualiza as contagens da agenda do dia (inclui tem_chamada)
      await this.fetchAgendaContagensPorData();
    },

    detalharAulaAvulsa(id) {
      // ajusta conforme sua rota de detalhe
      this.$router.push({ name: 'DetalharAulasAvulsas', params: { id } });
    },
    detalharAulaReposicao(id) {
      // supondo rota /operacional/aulas-reposicao/:id
      this.$router.push({ name: 'DetalharAulasReposicao', params: { id } });
    },

    async abrirReagendarReposicao(aula) {
      try {
        let full = aula

        if (!aula?.nivel_id || !aula?.turma_id || !aula?.instrutor_nome) {
          const { data } = await axios.get(`/operacional/aulas-reposicao/${aula.id}`)
          full = { ...aula, ...data }
        }

        this.selectedAulaReposicao = full
        this.showModalReagendarReposicao = true
      } catch (err) {
        console.error('[Dashboard] erro ao buscar detalhes da aula:', err)
        this.$alert?.('Não foi possível carregar os dados para reagendar.', 'Erro')
      }
    },

    async onReagendamentoSalvo() {
      this.showModalReagendarReposicao = false
      this.selectedAulaReposicao = null
      await this.fetchOverview?.()
    },
  },

  async mounted() {
    await this.fetchOverview();
    this.fetchData();
    await this.fetchAgendaContagensPorData(); // <<< ADICIONE ESTA LINHA
    // fallback: se a API do overview ainda não enviar 'faltalista', busca direto
    if (!this.overview.faltalista) {
      this.fetchFaltasNaoComunicadas();
    }
  },
};
</script>

<style scoped>
h5 {
  font-weight: bold;
  margin: 0 auto;
}

.icon-button {
  background: none;
  border: none;
  color: inherit;
  font-size: 1rem;
  cursor: pointer;
}
.icon-button:focus {
  outline: none;
}
.icon-button:hover {
  color: inherit;
}
.icon-button.mx-2 {
  font-size: 0.8rem;
  font-weight: normal;
}
.me-3 {
  margin-right: 1rem;
}
.ms-3 {
  margin-left: 1rem;
}

.table-scroll {
  max-height: 500px;   /* ajuste fino se quiser */
  overflow-y: auto;
}


/* força a cor no header do b-card */
:deep(.card-header.bg-orange) { background-color: #fd7e14 !important; }
/* se quiser garantir o texto em branco no título do header */
:deep(.card-header.bg-orange h6) { color: #fff !important; }

/* no mesmo componente (scoped) ou em um CSS global */
:deep(.bg-emerald) { background-color: #1ABC9C !important; }
:deep(.bg-brown) { background-color: #8B4513 !important; }

:deep(.badge-purple) {
  background-color: #6a1b9a !important;
  color: #fff !important;
}

:deep(.bg-purple) {
  background-color: #6a1b9a !important;
  color: #fff !important;
}

/* Header do card Protocolos de Queda */
:deep(.card-protocolo-queda .card-header) {
  background-color: #6a1b9a !important;
}

/* bolinhas de nível */
.dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.dot.vermelho { background: #dc3545; }
.dot.amarelo  { background: #ffc107; }
.dot.roxo     { background: #6a1b9a; }
.dot.verde    { background: #198754; }
.dot.normal   { background: #6c757d; }

.dashboard-card-blocked {
  opacity: 0.65;
  cursor: not-allowed;
}

.dashboard-card-blocked :deep(.card),
.dashboard-card-blocked :deep(.dashboard-card),
.dashboard-card-blocked :deep(table),
.dashboard-card-blocked :deep(button),
.dashboard-card-blocked :deep(a),
.dashboard-card-blocked :deep(i) {
  pointer-events: none;
}
</style>
